import { ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkingAreaRequestModel } from 'src/app/models/workingArea-request.model';
import { ToastService } from 'src/app/services/toast.service';
import { WorkloadsService } from 'src/app/services/workloads.service';
import { InduMapComponent } from 'src/app/shared/components/indu-map/indu-map.component';
import { AreaTypeEnum } from 'src/app/shared/enumerators/area-type.enum';
import { MatButton } from '@angular/material/button';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';

@Component({
  selector: 'app-franchise-zone',
  templateUrl: './franchise-zone.component.html',
  styleUrls: ['./franchise-zone.component.scss'],
  imports: [InduMapComponent, MatButton, PageHeaderComponent]
})
export class FranchiseZoneComponent implements OnInit {
  franchiseSvc = inject(WorkloadsService);
  activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  toastr = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild(InduMapComponent) mapComponent!: InduMapComponent;

  companyName: string = '';
  center!: google.maps.LatLngLiteral;
  circleCenter: any[] = [];
  franchiseId: string = '';
  franchiseZone: any;
  drawZone: boolean = true;

  ngOnInit() {
    // 1. Extraemos los parámetros de forma segura primero
    this.activatedRoute.queryParams.subscribe((params) => {
      let queryParams = { ...params };

      if (params['id'] && params['companyName']) {
        this.franchiseId = params['id'];
        this.companyName = params['companyName'];

        // 2. SOLO CUANDO TENEMOS EL ID, buscamos la zona
        this.getFranchiseZone(this.franchiseId);
      }

      // Limpieza de URL (tu lógica original)
      delete queryParams['companyName'];
      delete queryParams['id'];
      this.router.navigate(['.'], {
        relativeTo: this.activatedRoute,
        queryParams: queryParams,
        replaceUrl: true, // same page: no extra history entry for Back/Volver to land on
      });
    });
  }

  getFranchiseZone(franchiseId: string) {
    if (!franchiseId) return;

    this.franchiseSvc.getFranchiseZone(franchiseId).subscribe({
      next: (data) => {
        if (data) {
          this.drawZone = false;
          this.franchiseZone = data;

          this.center = {
            lat: data.latitude,
            lng: data.longitude,
          };

          if (this.mapComponent) {
            this.mapComponent.setMapCenter(this.center);
          }

          const circle = {
            id: data.id, // <-- Ahora le pasamos el ID real al mapa
            lat: data.latitude,
            lng: data.longitude,
            radiusKm: data.radiusKm,
            options: {
              fillColor: 'green',
              strokeColor: 'green',
              areaType: AreaTypeEnum.Inclusion,
              strokeOpacity: 0.8,
              strokeWeight: 1,
              fillOpacity: 0.35,
              clickable: true,
              editable: true,
              draggable: true,
              zIndex: 1,
            },
          };

          // Asignación inmutable para Angular
          this.circleCenter = [circle];
        }
      },
      error: (err) => {
        // 3. Manejo del 404: Si no hay zona, habilitamos el dibujo
        this.drawZone = true;
        this.circleCenter = [];
      },
    });
  }

  // Se dispara cada vez que el mapa emite un cambio (Dibujar, mover, redimensionar)
  setFranchiseZone(zoneData: any) {
    this.franchiseZone = zoneData;

    // Sincronizamos visualmente para que el mapa no se resetee
    const circle = {
      id: zoneData.id,
      lat: zoneData.latitude,
      lng: zoneData.longitude,
      radiusKm: zoneData.radiusKm,
      options: {
        fillColor: 'green',
        strokeColor: 'green',
        areaType: zoneData.areaType || 1,
        fillOpacity: 0.35,
        strokeWeight: 1,
        editable: true,
        draggable: true,
        clickable: true
      }
    };

    this.circleCenter = [circle];
    this.drawZone = false; // Ocultamos el botón de dibujar una vez que hay círculo
    this.cdr.detectChanges();
  }

  onSubmit() {
    if (!this.franchiseZone) {
      this.toastr.warning('Zona de acción', 'Dibujá un círculo sobre el mapa antes de confirmar.');
      return;
    }

    // Verificamos si es un ID temporal (recién dibujado) o no tiene ID
    const isNewZone = !this.franchiseZone.id || String(this.franchiseZone.id).startsWith('temp_');

    const payload: WorkingAreaRequestModel = {
      latitude: this.franchiseZone.latitude,
      longitude: this.franchiseZone.longitude,
      radiusKm: Number(this.franchiseZone.radiusKm), // Nos aseguramos de enviar el número limpio
      areaType: this.franchiseZone.areaType || 1
    };

    if (isNewZone) {
      // POST: Crear nueva zona
      this.franchiseSvc.addFranchiseZone(this.franchiseId, payload).subscribe({
        next: () => {
          this.toastr.success('Zona de acción', 'Se ha guardado correctamente.');
          this.getFranchiseZone(this.franchiseId); // Recargamos para obtener el ID real de la base de datos
        },
        error: () => this.toastr.error('Error', 'No se pudo guardar la zona.'),
      });
    } else {
      // PUT/PATCH: Actualizar zona existente
      // NOTA: Asumo que tienes un método 'updateFranchiseZone'. Si addFranchiseZone hace upsert, usa ese.
      this.franchiseSvc.updateFranchiseZone(this.franchiseId, payload).subscribe({
        next: () => this.toastr.success('Zona actualizada', 'Se han guardado los cambios.'),
        error: () => this.toastr.error('Error', 'No se pudieron actualizar los cambios.'),
      });
    }
  }

  deleteFranchiseZone() {
    // Si la zona era temporal (recién dibujada y no guardada), solo la borramos de la vista
    if (!this.franchiseZone || String(this.franchiseZone.id).startsWith('temp_')) {
      this.circleCenter = [];
      this.drawZone = true;
      this.franchiseZone = null;
      return;
    }

    // Si hay zona real guardada, llamo al endpoint
    this.franchiseSvc.deleteFranchiseZone(this.franchiseId).subscribe({
      next: () => {
        this.franchiseZone = null;
        this.circleCenter = [];
        this.drawZone = true;
        this.toastr.success('Zona eliminada', 'Se ha eliminado correctamente.');
      },
      error: () => {
        this.toastr.error('Error', 'No se pudo eliminar la zona.');
      },
    });
  }

  /** Leaves the zone editor without saving and returns to the franchise list. */
  onCancel() {
    this.router.navigate(['/franchises']);
  }
}
